// Registry of recovery artifact formats (AUTH-08).
//
// Append-only. Never edit or remove a row — a printed artifact depends on it.
// Strengthening means appending a row. The pin test
// (__tests__/recoverySchemes.pin.test.ts) holds a literal copy; a new row is
// appended to both. Go mirrors id/name/writable in
// internal/crypto/recovery_schemes.go (parity test reads this file, so keep each
// row head on one line: `{ id: <n>, name: '<s>', writable: <bool>, …`).
//
// Rows are data. Code reads row 0's `kdf` (index.ts) and row 1's `id`,
// `artifact.seedBytes`, `artifact.words`, `artifact.qrPrefix`, `derive.key`,
// `fingerprint.bits` and `fingerprint.groups` (recoveryKit.ts). The other fields
// are descriptive and pinned only by the pin test. Each row's `fixture` is a reference artifact under
// web/src/features/encryption/__fixtures__/ that referenceArtifacts.test.ts
// restores with real libsodium on every run.
//
// This module imports nothing, so index.ts and recoveryKit.ts can both read it
// without an import cycle.

type DeepReadonly<T> = { readonly [K in keyof T]: DeepReadonly<T[K]> };

function deepFreeze<T>(value: T): DeepReadonly<T> {
  if (value !== null && typeof value === 'object' && !Object.isFrozen(value)) {
    for (const v of Object.values(value as Record<string, unknown>)) deepFreeze(v);
    Object.freeze(value);
  }
  return value as DeepReadonly<T>;
}

export const RECOVERY_SCHEMES = deepFreeze([
  { id: 0, name: 'legacy-password-sheet', writable: false, fixture: 'legacy-sheet-v0.json',
    artifact: { kind: 'password-sheet', payloadBytes: 88, saltBytes: 16, wrappedBytes: 72 },
    kdf: { alg: 'argon2id13', algId: 2, opslimit: 3, memlimit: 268435456, keyBytes: 32 },
    aead: 'xchacha20poly1305-ietf' },
  { id: 1, name: 'kit-v1', writable: true, fixture: 'kit-v1.json',
    artifact: { kind: 'kit', seedBytes: 32, words: 24, wordlist: 'bip39-english', qrPrefix: 'LVDR-KIT:1:' },
    derive: { hash: 'blake2b', outBytes: 32, key: 'lvdr-recovery-kit-v1', keypair: 'crypto_box_seed_keypair' },
    fingerprint: { hash: 'blake2b-256', bits: 40, alphabet: 'crockford-base32-lower', groups: [4, 4] },
    wrap: 'box-nonce24-ephpub32' },
] as const);

export type RecoveryScheme = (typeof RECOVERY_SCHEMES)[number];

/** Row 0: the 88-byte password sheet. Read-only — a server never stores a new one. */
export const LEGACY_PASSWORD_SHEET = RECOVERY_SCHEMES[0];
/** Row 1: the 24-word / QR recovery kit. */
export const KIT_V1 = RECOVERY_SCHEMES[1];
